/*
 * <summerween-vanish> — the "Almost Vanished!" ember dissolve.
 *
 * Progressive enhancement. The real text inside [data-vanish-text] is the
 * accessible, no-JS default and stays exactly where it is. When motion is
 * allowed and the headline scrolls into view, we sample that text into a glowing
 * ember particle field on an overlay canvas and run a slow "tease" loop: the
 * words lift apart into embers, almost vanish, then drift back and reassemble.
 *
 * Discipline:
 *  - prefers-reduced-motion: reduce  -> no canvas at all, text stays put.
 *  - Off-screen (IntersectionObserver) or tab hidden -> loop fully paused.
 *  - Lazy-built only when near the viewport; rebuilt on resize.
 *  - Font is read from getComputedStyle + document.fonts.ready, never hardcoded,
 *    so the canvas matches whatever --font-heading-family resolves to.
 */
if (!customElements.get('summerween-vanish')) {
  const REST = 4200; // ms the words sit solid before a tease
  const FADE = 170; // crossfade between real text and particle field
  const DISPERSE = 2500; // embers lift and scatter (slowed down)
  const HOLD = 1300; // the "almost gone" beat
  const REFORM = 3200; // embers drift back and reassemble (slowed down)
  const REFORM_EASE = 'cubic-bezier(0.33, 1, 0.68, 1)'; // CSS ease-out-cubic
  const PARTICLE_PHASE = DISPERSE + HOLD + REFORM;
  const CYCLE = REST + PARTICLE_PHASE;
  const MIN_ALPHA = 0; // can vanish completely now
  const MAX_PARTICLES = 4500; // Increased for high resolution

  const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
  const easeInQuad = (t) => t * t;

  const makeSprite = (r, g, b) => {
    const s = 18;
    const c = document.createElement('canvas');
    c.width = c.height = s;
    const x = c.getContext('2d');
    const grd = x.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    grd.addColorStop(0, `rgba(${r},${g},${b},1)`);
    grd.addColorStop(0.35, `rgba(${r},${g},${b},0.62)`);
    grd.addColorStop(1, `rgba(${r},${g},${b},0)`);
    x.fillStyle = grd;
    x.fillRect(0, 0, s, s);
    return c;
  };

  customElements.define(
    'summerween-vanish',
    class SummerweenVanish extends HTMLElement {
      constructor() {
        super();
        this._text = this.querySelector('[data-vanish-text]');
        this._raf = 0;
        this._particles = [];
        this._running = false;
        this._inView = false;
        this._built = false;
        this._building = false;
        this._phase = '';
        this._cycleStart = 0;
        this._frame = this._frame.bind(this);
        this._onResize = this._debounce(() => this._rebuild(), 220);
      }

      connectedCallback() {
        if (!this._text) return;
        this._reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (this._reduceMotion.matches) return; // static text wins

        this._spriteCore = makeSprite(250, 242, 232); // warm-white letter body
        this._spriteEmber = makeSprite(255, 150, 46); // pumpkin ember glow

        this._io = new IntersectionObserver(
          (entries) => {
            this._inView = entries[0].isIntersecting;
            if (this._inView) this._start();
            else this._stop();
          },
          { rootMargin: '200px' }
        );
        this._io.observe(this);

        this._ro = new ResizeObserver(this._onResize);
        this._ro.observe(this);

        this._visHandler = () => {
          if (document.hidden) this._stop();
          else if (this._inView) this._start();
        };
        document.addEventListener('visibilitychange', this._visHandler);

        // If reduced-motion preference flips on, tear the effect down.
        this._mmHandler = () => {
          if (this._reduceMotion.matches) {
            this._stop();
            this._teardownCanvas();
          }
        };
        this._reduceMotion.addEventListener?.('change', this._mmHandler);
      }

      disconnectedCallback() {
        this._stop();
        this._io?.disconnect();
        this._ro?.disconnect();
        this._teardownCanvas();
        if (this._visHandler) document.removeEventListener('visibilitychange', this._visHandler);
        if (this._mmHandler) this._reduceMotion?.removeEventListener?.('change', this._mmHandler);
      }

      _debounce(fn, ms) {
        let id;
        return () => {
          clearTimeout(id);
          id = setTimeout(fn, ms);
        };
      }

      _start() {
        if (this._running || this._reduceMotion.matches || document.hidden) return;
        this._running = true;
        const run = () => {
          if (!this._running) return;
          this._cycleStart = 0;
          this._phase = '';
          this._raf = requestAnimationFrame(this._frame);
        };
        if (this._built) run();
        else this._build().then((ok) => ok && this._running && run());
      }

      _stop() {
        this._running = false;
        if (this._raf) {
          cancelAnimationFrame(this._raf);
          this._raf = 0;
        }
        this._cycleStart = 0;
        this._phase = '';
        // The canvas owns the headline, so leave a static crisp frame painted
        // while paused (off-screen / hidden) instead of clearing to nothing.
        if (this._ctx && this._built) {
          this._resetCanvasOpacity();
          this._ctx.clearRect(0, 0, this._cwCss, this._chCss);
          this._drawCrisp(1);
        }
      }

      // Cancels any in-flight reform fade and snaps the canvas element back to
      // fully visible, so a paused/rest/disperse frame is never left translucent.
      _resetCanvasOpacity() {
        if (!this._canvas) return;
        this._canvas.style.transition = 'none';
        this._canvas.style.opacity = '1';
      }

      _teardownCanvas() {
        if (this._canvas) this._canvas.remove();
        this._canvas = null;
        this._ctx = null;
        this._particles = [];
        this._built = false;
        if (this._text) this._text.style.opacity = '';
      }

      _rebuild() {
        const wasRunning = this._running;
        this._stop();
        this._teardownCanvas();
        if (wasRunning && this._inView) this._start();
      }

      async _build() {
        if (this._building || this._reduceMotion.matches) return false;
        this._building = true;
        try {
          if (document.fonts && document.fonts.ready) await document.fonts.ready;
        } catch (e) {
          /* ignore */
        }

        const cs = getComputedStyle(this._text);
        const rect = this._text.getBoundingClientRect();
        const w = rect.width;
        const h = rect.height;
        if (w < 2 || h < 2) {
          this._building = false;
          return false; // not laid out yet
        }

        const padX = Math.max(24, w * 0.4);
        const padTop = Math.max(64, h * 1.7); // embers rise, so room above
        const padBottom = Math.max(20, h * 0.5);
        const cwCss = w + padX * 2;
        const chCss = h + padTop + padBottom;
        const dpr = Math.min(2, window.devicePixelRatio || 1);

        // --- sample the text into an alpha mask ---
        const sCanvas = document.createElement('canvas');
        sCanvas.width = Math.ceil(cwCss * dpr);
        sCanvas.height = Math.ceil(chCss * dpr);
        const sctx = sCanvas.getContext('2d');
        sctx.scale(dpr, dpr);
        sctx.fillStyle = '#fff';
        // Align on the alphabetic baseline, not 'top'. getBoundingClientRect()
        // returns the font's bounding box (its top = host y 0 = canvas padTop), so
        // placing the baseline at padTop + fontBoundingBoxAscent reproduces exactly
        // where the browser paints the glyphs. (textBaseline 'top' assumes em-box
        // top == line-box top, which is false for display faces with tall metrics
        // like Shrikhand, and makes the particles sit a few px high — a visible
        // upward jump when the field crossfades in.)
        const fontStr = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
        sctx.textBaseline = 'alphabetic';
        sctx.textAlign = 'left';
        sctx.font = fontStr;
        if ('letterSpacing' in sctx) {
          try {
            sctx.letterSpacing = cs.letterSpacing;
          } catch (e) {
            /* unsupported — negligible */
          }
        }
        let txt = this._text.textContent || '';
        if (cs.textTransform === 'uppercase') txt = txt.toUpperCase();
        else if (cs.textTransform === 'lowercase') txt = txt.toLowerCase();
        const fm = sctx.measureText(txt);
        const ascent = fm.fontBoundingBoxAscent || fm.actualBoundingBoxAscent || parseFloat(cs.fontSize) * 0.8;
        sctx.fillText(txt, padX, padTop + ascent);

        // Stash everything the crisp resting paint needs so it lands on the exact
        // same pixels the particles were sampled from (no swap, no jump).
        this._txt = txt;
        this._fontStr = fontStr;
        this._letterSpacing = cs.letterSpacing;
        this._padX = padX;
        this._baselineY = padTop + ascent;
        this._inkColor = cs.color;
        this._shadow = this._parseShadow(cs.textShadow);

        const data = sctx.getImageData(0, 0, sCanvas.width, sCanvas.height).data;
        const fontPx = parseFloat(cs.fontSize) || 48;
        // Choose a gap that keeps the particle budget bounded across sizes.
        const targetGap = Math.sqrt((w * h * 0.34) / Math.min(MAX_PARTICLES, 4500));
        const gap = Math.max(1, Math.round(Math.max(targetGap, fontPx / 35)));
        const stepDev = Math.max(1, Math.round(gap * dpr));

        const particles = [];
        for (let y = 0; y < sCanvas.height; y += stepDev) {
          for (let x = 0; x < sCanvas.width; x += stepDev) {
            if (data[(y * sCanvas.width + x) * 4 + 3] > 130) {
              const ox = x / dpr;
              const oy = y / dpr;
              
              // Wind blowing left to right
              const windBlow = w * (0.3 + Math.random() * 0.8); 
              const lift = h * (0.1 + Math.random() * 0.5); 
              const spreadY = (Math.random() - 0.5) * h * 0.4;
              
              const normX = clamp01((ox - padX) / w);
              const sweepDelay = normX * 0.4 + Math.random() * 0.15;

              particles.push({
                ox,
                oy,
                sx: ox + windBlow,
                sy: oy - lift + spreadY,
                size: 0.6 + Math.random() * 1.8,
                delay: sweepDelay,
                tw: Math.random() * Math.PI * 2,
                twSpd: 6 + Math.random() * 8,
                ember: Math.random(),
                vanishEnd: 0.6 + Math.random() * 0.4, // organic burnout time
              });
            }
          }
        }
        this._particles = particles;

        // --- overlay canvas, parked exactly over the text ---
        let canvas = this._canvas;
        if (!canvas) {
          canvas = document.createElement('canvas');
          canvas.className = 'summerween-masthead__vanish-canvas';
          canvas.setAttribute('aria-hidden', 'true');
          this.appendChild(canvas);
          this._canvas = canvas;
        }
        canvas.width = Math.ceil(cwCss * dpr);
        canvas.height = Math.ceil(chCss * dpr);
        canvas.style.width = `${cwCss}px`;
        canvas.style.height = `${chCss}px`;
        canvas.style.left = `${-padX}px`;
        canvas.style.top = `${-padTop}px`;
        const ctx = canvas.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        this._ctx = ctx;
        this._cwCss = cwCss;
        this._chCss = chCss;

        this._built = true;
        this._building = false;

        // Paint the crisp text immediately, then hand the headline to the canvas
        // by hiding the real text (it stays in the DOM for a11y / no-JS / SEO).
        this._drawCrisp(1);
        this._text.style.opacity = '0';
        return true;
      }

      _parseShadow(str) {
        if (!str || str === 'none') return null;
        const m = str.match(/(rgba?\([^)]+\)|#[0-9a-f]+)\s+(-?[\d.]+)px\s+(-?[\d.]+)px\s+(-?[\d.]+)px/i);
        if (!m) return null;
        return { color: m[1], x: parseFloat(m[2]), y: parseFloat(m[3]), blur: parseFloat(m[4]) };
      }

      _drawCrisp(alpha) {
        const ctx = this._ctx;
        if (!ctx) return;
        ctx.save();
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = alpha;
        ctx.fillStyle = this._inkColor;
        ctx.textBaseline = 'alphabetic';
        ctx.textAlign = 'left';
        ctx.font = this._fontStr;
        if ('letterSpacing' in ctx) {
          try {
            ctx.letterSpacing = this._letterSpacing;
          } catch (e) {
            /* unsupported */
          }
        }
        if (this._shadow) {
          ctx.shadowColor = this._shadow.color;
          ctx.shadowBlur = this._shadow.blur;
          ctx.shadowOffsetX = this._shadow.x;
          ctx.shadowOffsetY = this._shadow.y;
        }
        ctx.fillText(this._txt, this._padX, this._baselineY);
        ctx.restore();
      }

      _frame(now) {
        if (!this._running) return;
        this._raf = requestAnimationFrame(this._frame);
        const ctx = this._ctx;
        if (!ctx) return;

        if (!this._cycleStart) this._cycleStart = now;
        let t = now - this._cycleStart;
        if (t >= CYCLE) {
          this._cycleStart = now;
          t = 0;
        }

        // Resting: paint the crisp text once (the canvas always owns the headline).
        if (t < REST) {
          if (this._phase !== 'rest') {
            this._phase = 'rest';
            this._resetCanvasOpacity();
            ctx.clearRect(0, 0, this._cwCss, this._chCss);
            this._drawCrisp(1);
          }
          return;
        }

        const pt = t - REST; // 0..PARTICLE_PHASE

        if (pt <= DISPERSE + HOLD) {
          // Seamless disintegration: erase solid text softly from left to right
          // while embers lift away. Redrawn every frame.
          if (this._phase === 'reform') this._resetCanvasOpacity();
          this._phase = 'particles';
          ctx.clearRect(0, 0, this._cwCss, this._chCss);

          // Wind sweeps across in the first 80% of DISPERSE
          const windProg = clamp01(pt / (DISPERSE * 0.8));
          const windFront = windProg * this._cwCss; // move cut across the canvas

          this._drawCrisp(1);

          // Softly erase the crisp text from left to right to reveal the particles blowing away
          if (windFront > 0) {
            ctx.globalCompositeOperation = 'destination-out';
            const grad = ctx.createLinearGradient(windFront - 60, 0, windFront + 20, 0);
            grad.addColorStop(0, 'rgba(0,0,0,1)'); // fully erased
            grad.addColorStop(1, 'rgba(0,0,0,0)'); // still crisp
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, this._cwCss, this._chCss);
            ctx.globalCompositeOperation = 'source-over';
          }

          ctx.globalCompositeOperation = 'lighter';
          const ps = this._particles;
          const core = this._spriteCore;
          const ember = this._spriteEmber;
          for (let i = 0; i < ps.length; i++) {
            const p = ps[i];

            let prog;
            if (pt <= DISPERSE) {
              prog = easeInQuad(clamp01((pt / DISPERSE - p.delay) / (1 - p.delay)));
            } else {
              prog = 1;
            }

            let x = p.ox + (p.sx - p.ox) * prog;
            let y = p.oy + (p.sy - p.oy) * prog;

            const twinkle = 0.75 + 0.25 * Math.sin(p.tw + now * 0.001 * p.twSpd);

            // Disappear completely with organic variance
            const alphaProg = clamp01(prog / p.vanishEnd);
            const a = (1 - alphaProg) * twinkle;
            if (a <= 0.005) continue;

            // High resolution shrink as they vanish
            const sizeMultiplier = 1 - alphaProg * 0.9;
            const coreSz = p.size * (1 + prog * 0.6) * sizeMultiplier;
            ctx.globalAlpha = a;
            ctx.drawImage(core, x - coreSz, y - coreSz, coreSz * 2, coreSz * 2);

            if (prog > 0.02) {
              const glowSz = p.size * (1.4 + prog * 2.2) * sizeMultiplier;
              ctx.globalAlpha = a * prog * 0.85 * (0.5 + p.ember * 0.8);
              ctx.drawImage(ember, x - glowSz, y - glowSz, glowSz * 2, glowSz * 2);
            }
          }
          ctx.globalAlpha = 1;
          ctx.globalCompositeOperation = 'source-over';
        } else if (this._phase !== 'reform') {
          // Re-assembly: draw the crisp text once at full strength, then let the
          // canvas element's own CSS opacity fade it in. iOS Safari ignores
          // ctx.globalAlpha on fillText when shadowBlur is set, so the previous
          // per-frame canvas-alpha ramp snapped straight to fully visible there;
          // an element-level opacity transition is unaffected by that bug.
          this._phase = 'reform';
          ctx.clearRect(0, 0, this._cwCss, this._chCss);
          this._drawCrisp(1);

          const canvas = this._canvas;
          canvas.style.transition = 'none';
          canvas.style.opacity = '0';
          void canvas.offsetHeight; // flush so the transition below animates from 0
          canvas.style.transition = `opacity ${REFORM}ms ${REFORM_EASE}`;
          canvas.style.opacity = '1';
        }
      }
    }
  );
}
